import dayjs from "dayjs";
import { Agent as BaseAgent } from "../../../../interfaces/agent.interface";

/** An eligible partner; referral and sticky partners can also carry the Trip Advisor who owns the link. */
export type Agent = BaseAgent & { subagent?: Partial<BaseAgent> & { full_name?: string } };
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";
import { PaxCounts, PAX_TYPES } from "../components/ChooseYourTour";

export interface Passenger {
  designation: string;
  surname: string;
  given_name: string;
  mobile: string;
  email: string;
  category: keyof PaxCounts;
  nationality: string;
  date_of_birth: string;
  customer_remark: string;
  wechat_qrcode?: string;
  wechat_qrcode_name?: string;
  passport_number?: string;
  as_passport_photocopy?: string;
  as_passport_photocopy_name?: string;
  /** Passenger 2+: reuse Passenger 1's mobile and email. */
  same_as_first?: boolean;
}

export interface FlightDetails {
  flight_departure_date: string;
  depart_origin_destination: string;
  departure_flight_no: string;
  depart_dept_arrival_time: string;
  flight_return_date: string;
  return_origin_destination: string;
  return_flight_no: string;
  return_departure_arrv_time: string;
}

export interface RoomOption {
  /** Value sent to the backend (same casing B2B stores). */
  label: string;
  /** Figma wording, e.g. "2 Triple, 2 Twin/Double". */
  display: string;
  total: number;
}

export const emptyPassenger = (category: keyof PaxCounts): Passenger => ({
  designation: "", surname: "", given_name: "", mobile: "", email: "", category,
  nationality: "", date_of_birth: "", customer_remark: "",
});

export const passengerCategories = (counts: PaxCounts) =>
  PAX_TYPES.flatMap(({ key }) => Array.from({ length: counts[key] }, () => key));

// Same rules as the B2B land tour booking form (leftPanel.js#handleRooms): adults and twin-sharing children
// fill twin/triple rooms, a child with an extra bed needs a twin room to share, and children without a bed
// and infants take the spare third place of a triple. Falls back to plain twin rooms when no such split works.
export const roomConfigurations = (counts: PaxCounts): RoomOption[] => {
  const adultTwin = counts.adult + counts.child_twin;
  const singles = counts.single_supplement;
  const noBed = counts.child_without_bed + counts.infant;
  const withBed = counts.child_with_bed;
  const labels: { upper: string; display: string; total: number }[] = [];
  const build = (triples: number, twins: number) => {
    const parts: [number, string, string][] = [
      [triples, "TRIPLE", "Triple"], [twins, "TWIN/DOUBLE", "Twin/Double"], [singles, "SINGLE", "Single"],
    ];
    const used = parts.filter(([count]) => count > 0);
    return {
      upper: used.map(([count, upper]) => `${count} ${upper}`).join(", "),
      display: used.map(([count, , text]) => `${count} ${text}`).join(", "),
      total: triples + twins + singles,
    };
  };

  if (adultTwin > 0) {
    for (let triples = Math.floor(adultTwin / 3); triples >= 0; triples -= 1) {
      const twins = (adultTwin - 3 * triples) / 2;
      if (!Number.isInteger(twins)) continue;
      const leftNoBed = noBed - triples;
      const enough = leftNoBed >= 0 ? twins >= leftNoBed + withBed : twins >= withBed;
      if (enough) labels.push(build(triples, twins));
    }
  }

  if (!labels.length) {
    const beds = adultTwin + withBed;
    const pairs = Math.floor(beds / 2);
    const odd = beds % 2;
    if (noBed <= pairs + odd + singles && pairs + odd + singles > 0) {
      // B2B lists the odd bed as its own "1 TWIN/DOUBLE" entry, so the wording is kept identical.
      const parts: [number, string, string][] = [[pairs, "TWIN/DOUBLE", "Twin/Double"], [odd, "TWIN/DOUBLE", "Twin/Double"], [singles, "SINGLE", "Single"]];
      const used = parts.filter(([count]) => count > 0);
      labels.push({
        upper: used.map(([count, upper]) => `${count} ${upper}`).join(", "),
        display: used.map(([count, , text]) => `${count} ${text}`).join(", "),
        total: pairs + odd + singles,
      });
    }
  }
  if (adultTwin + singles === 0) return [];

  return labels
    .filter((item, index) => item.upper && labels.findIndex((other) => other.upper === item.upper) === index)
    .map((item) => ({ label: item.upper, display: item.display, total: item.total }));
};

export const INVALID_ROOM_MESSAGE =
  "These travellers cannot be placed in a valid room combination. Please change the traveller types (for example, add an adult or remove a child or infant) to continue.";

/** "2 Child with Extra bed, 4 Adult (Single Room)" - the Figma travellers summary. */
export const travellersSummary = (counts: PaxCounts): string =>
  PAX_TYPES
    .filter(({ key }) => counts[key] > 0)
    .map(({ key, label }) => `${counts[key]} ${label}`)
    .join(", ");

export const defaultReturnDate = (departDate: string, landTour: LandTourDetail): string =>
  dayjs(departDate).add(Math.max((landTour.duration || landTour.days || 1) - 1, 0), "day").format("YYYY-MM-DD");

export const formatDate = (value?: string) => (value ? dayjs(value).format("DD MMM YYYY") : "");

export const bindingAgent = (response: any, agents: Agent[]): Agent | null => {
  const data = response?.data || response;
  const attrs = data?.attributes || data;
  if (!attrs) return null;
  const entityId = attrs.referral_entity_id || attrs.agent_id || attrs.referral_agent_id;
  const entityType = String(attrs.referral_entity_type || attrs.agent_type || "").toLowerCase();
  const embedded = attrs.bound_agent || attrs.agent || attrs.referral_agent || attrs.bound_subagent;
  const match = agents.find((agent) => entityType === "agency"
    ? String(agent.agency_id) === String(entityId)
    : String(agent.id) === String(entityId));
  if (match) return match;
  const id = Number(embedded?.id || entityId);
  if (!Number.isFinite(id)) return null;
  return {
    id,
    agency_id: embedded?.agency_id,
    name: embedded?.full_name || embedded?.name || attrs.referral_entity_name || attrs.agent_name || "Preferred Partner",
    email: embedded?.email || attrs.referral_entity_email || "",
    address: embedded?.address || "",
    state: embedded?.state || "",
    city: embedded?.city,
    phone: embedded?.phone,
    username: embedded?.username,
    agency_full_name: embedded?.agency_full_name,
    subagent: attrs.bound_subagent || attrs.subagent,
  } as Agent;
};

/** Passengers as they are sent and validated: "same as Passenger 1" copies the first passenger's contact details. */
export const effectivePassengers = (passengers: Passenger[]): Passenger[] =>
  passengers.map((passenger, index) =>
    index > 0 && passenger.same_as_first
      ? { ...passenger, mobile: passengers[0]?.mobile || "", email: passengers[0]?.email || "" }
      : passenger);

const DEFAULT_CHILD_RANGE = { min_age: 2, max_age: 11 };

/** Inclusive age window (in whole years at departure) the passenger's fare category allows. */
export const ageWindow = (category: keyof PaxCounts, landTour: LandTourDetail): { min: number; max?: number } => {
  if (category === "infant") return { min: 0, max: 1 };
  if (category.startsWith("child")) {
    const range = landTour.childAgeRanges?.[category];
    return { min: range?.min_age ?? DEFAULT_CHILD_RANGE.min_age, max: range?.max_age ?? DEFAULT_CHILD_RANGE.max_age };
  }
  const oldestChild = Math.max(
    DEFAULT_CHILD_RANGE.max_age,
    ...Object.values(landTour.childAgeRanges || {}).map((range) => range?.max_age ?? 0),
  );
  return { min: oldestChild + 1 };
};

/** True when a date of birth is not allowed for this fare category on the departure date. */
export const dobDisabled = (current: dayjs.Dayjs, category: keyof PaxCounts, departDate: string, landTour: LandTourDetail): boolean => {
  if (!current) return false;
  if (current.isAfter(dayjs().endOf("day"))) return true;
  const departure = dayjs(departDate);
  const { min, max } = ageWindow(category, landTour);
  const youngest = departure.subtract(min, "year");
  const oldest = max === undefined ? undefined : departure.subtract(max + 1, "year").add(1, "day");
  return current.isAfter(youngest, "day") || (!!oldest && current.isBefore(oldest, "day"));
};

export const ageErrorMessage = (category: keyof PaxCounts, landTour: LandTourDetail): string => {
  const { min, max } = ageWindow(category, landTour);
  if (category === "infant") return "Infants must be under 2 years old on the departure date.";
  return max === undefined ? `Travellers must be ${min} years or older on the departure date.` : `Age on the departure date must be ${min}-${max} years.`;
};

export const passengerMissing = (passenger: Passenger, index: number, landTour: LandTourDetail): string[] => {
  const missing: string[] = [];
  if (!passenger.surname) missing.push("surname");
  if (!passenger.given_name) missing.push("given_name");
  if (!passenger.date_of_birth) missing.push("date_of_birth");
  if (!passenger.mobile) missing.push("mobile");
  if (!passenger.email) missing.push("email");
  if (!passenger.nationality) missing.push("nationality");
  if (index === 0 && landTour.wechatQrcodeRequired && !passenger.wechat_qrcode) missing.push("wechat_qrcode");
  return missing;
};

const flightLine = (...parts: (string | undefined)[]) => parts.filter(Boolean).join("  •  ") || "Flight number to be confirmed";

/** Outbound/return lines for the summaries: from the tour's own flights when included, else what the customer typed. */
export const flightSummary = (landTour: LandTourDetail, departDate: string, flights: FlightDetails) =>
  landTour.incFlight
    ? {
        outbound: { date: departDate, line: flightLine(landTour.flightDepartOriginDestination, landTour.flightDepartureNo, landTour.flightDepartArrivalTime) },
        inbound: { date: defaultReturnDate(departDate, landTour), line: flightLine(landTour.flightReturnOriginDestination, landTour.flightReturnNo, landTour.flightReturnArrivalTime) },
      }
    : {
        outbound: { date: flights.flight_departure_date, line: flightLine(flights.depart_origin_destination, flights.departure_flight_no, flights.depart_dept_arrival_time) },
        inbound: { date: flights.flight_return_date, line: flightLine(flights.return_origin_destination, flights.return_flight_no, flights.return_departure_arrv_time) },
      };

/** Location filter bucket: Selangor and Kuala Lumpur share "Klang Valley", as in the Series partner list. */
export const agentLocation = (agent: Pick<Agent, "state">): string => {
  const state = agent.state || "Other";
  return state === "Selangor" || state.includes("Kuala Lumpur") ? "Klang Valley" : state;
};

/** The reasons offered when asking to be released from a sticky partner (StickyAgentChangeRequests::Create). */
export const DEFAULT_CHANGE_REASONS: { value: string; label: string }[] = [
  { value: "agent_unresponsive", label: "Agent is unresponsive or hard to reach" },
  { value: "agent_inactive", label: "Agent is no longer handling my booking" },
  { value: "incorrect_info", label: "I was given incorrect information by my agent" },
  { value: "prefer_different", label: "I prefer to work with a different agent" },
  { value: "agent_recommendation", label: "My agent recommended I switch to another agent" },
  { value: "others", label: "Other reason" },
];
